import { Fragment, ReactElement, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from '../../../../../Styles/themed-styled-components';

import { AutocompleteOption, FormattedTreeViewItemData } from '@breathelife/ui-components';
import { PartIdentifierTag, SectionGroupBlueprint, SectionGroupKey } from '@zinnia/form-engine-sdk';

import { useUpdateQuestionnaireElementBlueprint } from '../../../../../ReactQuery/Admin/Questionnaire/questionnaireVersion.mutations';
import { QuestionnaireEditorContext } from '../../ContextProvider/QuestionnaireEditorContextProvider';
import { QuestionnaireVersionDataContext } from '../../ContextProvider/QuestionnaireVersionDataContextProvider';
import { CreateQuestionnaireElement } from '../Creators/CreateQuestionnaireElement';
import { SectionGroupModalEditor } from '../Editors/SectionGroupModalEditor';
import { makePartIdentifierFromTreePath } from '../Helpers/makePartIdentifierFromTreePath';
import { useQuestionnaireElementActions } from '../Hooks/useQuestionnaireElementActions';
import { useResize } from '../Hooks/useResize';
import { QuestionnaireSectionGroupTree } from './QuestionnaireSectionGroupTree';
import { QuestionnaireTreeViewSearchContext } from './QuestionnaireTreeViewSearchContextProvider';

const Container = styled.div<{ containerHeight?: number }>`
  min-height: 150px;
  height: ${(props) => (props.containerHeight ? `${props.containerHeight}px` : 'auto')};
  width: 100%;
  padding-top: 16px;
  padding-bottom: 4px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;

  ${(props) =>
    props.containerHeight === undefined &&
    css`
      flex: 1 1 auto;
    `}
`;

const ResizeHandle = styled.button<{ $isResizing: boolean }>`
  height: 4px;
  width: 100%;
  padding: 0px;
  border: 0px;
  background-color: ${(props) => props.theme.colors.grey[40]};
  cursor: ${(props) => (props.$isResizing ? 'row-resize' : 'ns-resize')};
  position: absolute;
  left: 0;
  bottom: 0;

  &:before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 2px;
    height: 2px;
    background-color: ${(props) => props.theme.colors.grey[40]};
    pointer-events: none;
    opacity: ${(props) => (props.$isResizing ? 1 : 0)};
  }

  &:hover {
    &:before {
      opacity: 1;
    }
  }
`;

type QuestionnaireTreeViewProps = {
  isEditingEnabled: boolean;
  dataLabelOptions: AutocompleteOption[];
};

const TREEVIEW_HEIGHT = 350;

function ResizableSectionGroup({ children }: { children: ReactNode }): ReactElement {
  const { size, ref, isResizing, enableResize } = useResize({ minSize: TREEVIEW_HEIGHT, isHeight: true });
  return (
    <Fragment>
      <Container ref={ref} containerHeight={size}>
        {children}
        <ResizeHandle type='button' $isResizing={isResizing} onMouseDown={enableResize} />
      </Container>
    </Fragment>
  );
}

export function QuestionnaireTreeView(props: QuestionnaireTreeViewProps): ReactElement {
  const { isEditingEnabled, dataLabelOptions } = props;

  const { searchResults, searchTerm, previousSearchTerm } = useContext(QuestionnaireTreeViewSearchContext);

  const { questionnaireVersionData, questionnaireVersionId } = useContext(QuestionnaireVersionDataContext);
  const questionnaireBlueprint = questionnaireVersionData?.blueprint;
  const { onItemClick, addItem } = useQuestionnaireElementActions({
    isQuestionnaireEditingEnabled: isEditingEnabled,
  });

  const { t } = useTranslation();

  const { selectedPartIdentifier } = useContext(QuestionnaireEditorContext);
  const [selectedItemTreePathIdentifier, setSelectedItemTreePathIdentifier] = useState<string | undefined>();
  const [forcedScrollToItemPathIdentifier, setForcedScrollToItemPathIdentifier] = useState<string | undefined>();

  const [isAddSectionModalVisible, setIsAddSectionModalVisible] = useState<boolean>(false);
  const [addSectionSectionGroupKey, setAddSectionSectionGroupKey] = useState<SectionGroupKey>('insuredPeople');

  const [isEditSectionGroupModalOpen, setIsEditSectionGroupModalOpen] = useState<boolean>(false);
  const [editSectionGroupKey, setEditSectionGroupKey] = useState<SectionGroupKey>('insuredPeople');

  const updateQuestionnaireElement = useUpdateQuestionnaireElementBlueprint(questionnaireVersionId);

  const onEditSectionGroup = useCallback(
    (sectionGroupBlueprint: SectionGroupBlueprint): void => {
      void updateQuestionnaireElement({
        partIdentifier: { tag: PartIdentifierTag.sectionGroup, sectionGroupPartName: editSectionGroupKey },
        sectionGroupKey: editSectionGroupKey,
        update: sectionGroupBlueprint,
      });
    },
    [editSectionGroupKey, updateQuestionnaireElement],
  );

  const handleItemClick = useCallback(
    (data: FormattedTreeViewItemData) => {
      const partIdentifier = makePartIdentifierFromTreePath(data.path);
      setSelectedItemTreePathIdentifier(data.pathIdentifier);
      onItemClick(partIdentifier);
    },
    [onItemClick],
  );

  useEffect(() => {
    if (!searchTerm && previousSearchTerm && selectedItemTreePathIdentifier) {
      setForcedScrollToItemPathIdentifier(selectedItemTreePathIdentifier);
    }
  }, [searchTerm, setForcedScrollToItemPathIdentifier, selectedItemTreePathIdentifier, previousSearchTerm]);

  const handleEditSectionGroupModal = useCallback((sectionGroupKey: SectionGroupKey) => {
    setEditSectionGroupKey(sectionGroupKey);
    setIsEditSectionGroupModalOpen(true);
  }, []);

  const handleOpenNewSectionModal = useCallback((sectionGroupKey: SectionGroupKey) => {
    setAddSectionSectionGroupKey(sectionGroupKey);
    setIsAddSectionModalVisible(true);
  }, []);

  function makeSectionGroupTree(sectionGroupKey: SectionGroupKey): JSX.Element {
    const sectionBlueprints =
      questionnaireVersionData?.blueprint.sectionBlueprints?.filter(
        (sectionBlueprint) => sectionBlueprint.sectionGroupKey === sectionGroupKey,
      ) || [];

    return (
      <QuestionnaireSectionGroupTree
        isEditingEnabled={isEditingEnabled}
        dataLabelOptions={dataLabelOptions}
        sectionGroupKey={sectionGroupKey}
        sectionGroupBlueprint={questionnaireBlueprint?.sectionGroupBlueprints[sectionGroupKey]}
        sectionsInGroupBlueprints={sectionBlueprints}
        forcedScrollToItemPathIdentifier={forcedScrollToItemPathIdentifier}
        onForceScrollToItemEnd={() => {
          setForcedScrollToItemPathIdentifier(undefined);
        }}
        handleItemClick={handleItemClick}
        openEditSectionGroupModal={() => {
          handleEditSectionGroupModal(sectionGroupKey);
        }}
        openAddSectionModal={() => {
          handleOpenNewSectionModal(sectionGroupKey);
        }}
        selectedPartIdentifier={selectedPartIdentifier}
        searchTerm={searchTerm}
        searchResults={searchResults}
      />
    );
  }

  const sectionGroups = Object.entries(questionnaireVersionData?.blueprint.sectionGroupBlueprints || {}).map(
    ([key]) => <ResizableSectionGroup key={key}>{makeSectionGroupTree(key as SectionGroupKey)}</ResizableSectionGroup>,
  );

  return (
    <Fragment>
      {sectionGroups}
      {isEditSectionGroupModalOpen && (
        <SectionGroupModalEditor
          isOpen={isEditSectionGroupModalOpen}
          isRepeatableSectionGroup={editSectionGroupKey === 'insuredPeople'}
          title={t('admin.questionnaireManagement.editSectionGroup')}
          sectionGroupBlueprint={questionnaireBlueprint?.sectionGroupBlueprints[editSectionGroupKey]}
          onSave={onEditSectionGroup}
          onClose={() => setIsEditSectionGroupModalOpen(false)}
        />
      )}

      {isAddSectionModalVisible && (
        <CreateQuestionnaireElement
          parentPartIdentifier={{
            tag: PartIdentifierTag.sectionGroup,
            sectionGroupPartName: addSectionSectionGroupKey,
          }}
          type={PartIdentifierTag.section}
          onCreate={(blueprint) => addItem(blueprint)}
          onClose={() => setIsAddSectionModalVisible(false)}
          dataLabelOptions={dataLabelOptions}
          sectionGroupKey={addSectionSectionGroupKey}
        />
      )}
    </Fragment>
  );
}
